import React, { useState, useEffect } from 'react';
import { Book, Save, X, User, FileText, Tag, AlertCircle, Check } from 'react-feather';
import { TagInput } from './TagInput';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface TeachingFormProps {
  originalQuestion: string;
  originalAnswer: string;
  conversationHistory: Message[];
  authenticatedUser: { id: string; name: string; email: string; role: string };
  onSubmit: (teachingData: any) => void;
  onCancel: () => void;
}

/**
 * Teaching Form Component
 * Like creating a treasure map to share knowledge with future pirates!
 */
const TeachingForm: React.FC<TeachingFormProps> = ({
  originalQuestion,
  originalAnswer,
  conversationHistory,
  authenticatedUser,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState({
    query: '',
    content: '',
    audience: 'customer' as 'internal' | 'customer',
    priority: 'medium' as 'critical' | 'high' | 'medium' | 'low',
    type: 'correction' as 'correction' | 'new_content' | 'clarification' | 'update',
    contributor: authenticatedUser.name,
    tags: [] as string[]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showPreview, setShowPreview] = useState(false);
  const [teachingPreview, setTeachingPreview] = useState<any>(null);
  const [previewStep, setPreviewStep] = useState<'form' | 'preview' | 'clarification'>('form');
  const [clarificationText, setClarificationText] = useState('');
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // Generate pre-load context from conversation
  const generatePreloadContext = (): string => {
    let context = `**Original Question:** ${originalQuestion}\n\n`;
    context += `**Original Answer:** ${originalAnswer}\n\n`;
    
    if (conversationHistory.length > 0) {
      context += `**Conversation Context:**\n`;
      conversationHistory.slice(-5).forEach((msg, index) => {
        const speaker = msg.type === 'user' ? 'User' : 'Assistant';
        const timestamp = msg.timestamp.toLocaleString();
        context += `\n${index + 1}. **${speaker}** (${timestamp}):\n${msg.content}\n`;
      });
    }
    
    return context;
  };

  const preloadContext = generatePreloadContext();

  // Initialize form with suggested values
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      query: originalQuestion || 'Teaching based on conversation context'
    }));
  }, [originalQuestion]);

  // Load available tags
  useEffect(() => {
    const loadTags = async () => {
      try {
        const response = await fetch('/api/teaching/tags');
        const data = await response.json();
        if (data.success && data.data && data.data.tags) {
          setAvailableTags(data.data.tags);
        }
      } catch (error) {
        console.error('Failed to load tags:', error);
      }
    };
    loadTags();
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.query.trim()) {
      newErrors.query = 'Query/question is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Teaching content is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generatePreview = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Generate teaching preview using the same flow as chat "teach:" command
      const previewData = {
        content: formData.content,
        query: formData.query,
        audience: formData.audience,
        priority: formData.priority,
        type: formData.type,
        contributor: {
          id: authenticatedUser.id,
          name: authenticatedUser.name,
          email: authenticatedUser.email,
          role: authenticatedUser.role
        },
        conversationContext: {
          question: originalQuestion,
          answer: originalAnswer,
          history: conversationHistory.slice(-5), // Last 5 messages for context
          fullContext: preloadContext
        }
      };
      
      const response = await fetch('/api/teaching/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(previewData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate teaching preview');
      }

      const result = await response.json();
      setTeachingPreview(result.data.preview);
      setPreviewStep('preview');
      
    } catch (error) {
      console.error('Error generating teaching preview:', error);
      setErrors({ submit: 'Failed to generate teaching preview. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!teachingPreview) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/teaching/approve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          preview: teachingPreview,  // Backend expects nested preview structure
          action: 'approve'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to approve teaching');
      }

      const result = await response.json();
      
      // Teaching has been successfully created, just notify parent it's done
      onSubmit(result.data);
      
    } catch (error) {
      console.error('Error approving teaching:', error);
      setErrors({ submit: 'Failed to approve teaching. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClarify = async () => {
    if (!teachingPreview || !clarificationText.trim()) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/teaching/approve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          preview: teachingPreview,
          action: 'clarify',
          clarification: clarificationText
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process clarification');
      }

      const result = await response.json();
      setTeachingPreview(result.data.preview);
      setClarificationText('');
      setPreviewStep('preview');
      
    } catch (error) {
      console.error('Error processing clarification:', error);
      setErrors({ submit: 'Failed to process clarification. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (previewStep === 'preview' || previewStep === 'clarification') {
      setPreviewStep('form');
      setTeachingPreview(null);
      setClarificationText('');
    } else {
      onCancel();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-eddy-green text-white p-6 rounded-t-lg border-b border-eddy-green/80 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Book size={24} />
            <div>
              <h3 className="text-xl font-semibold">Create New Teaching</h3>
              <p className="text-white/90 text-sm">Help improve Eddy's knowledge base</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-eddy-green/80 rounded-lg transition-colors"
            title="Close form"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {previewStep === 'form' && (
            <form onSubmit={generatePreview} className="p-6 space-y-6">
            {/* Error Display */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3">
                <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Submission Error</p>
                  <p className="text-sm">{errors.submit}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Form Fields */}
              <div className="space-y-4">
                {/* Query Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileText size={16} className="inline mr-2" />
                    Query/Question *
                  </label>
                  <textarea
                    value={formData.query}
                    onChange={(e) => handleInputChange('query', e.target.value)}
                    placeholder="What question does this teaching answer?"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-eddy-green focus:border-transparent ${
                      errors.query ? 'border-red-500' : 'border-gray-300'
                    }`}
                    rows={3}
                    required
                  />
                  {errors.query && (
                    <p className="text-red-600 text-sm mt-1">{errors.query}</p>
                  )}
                </div>

                {/* Content Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Book size={16} className="inline mr-2" />
                    Teaching Content *
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                    placeholder="Provide the correct information or clarification..."
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-eddy-green focus:border-transparent ${
                      errors.content ? 'border-red-500' : 'border-gray-300'
                    }`}
                    rows={8}
                    required
                  />
                  {errors.content && (
                    <p className="text-red-600 text-sm mt-1">{errors.content}</p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    Provide accurate information that corrects or enhances the AI's knowledge
                  </p>
                </div>

                {/* Form Controls Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Audience */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Audience
                    </label>
                    <select
                      value={formData.audience}
                      onChange={(e) => handleInputChange('audience', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-green focus:border-transparent"
                    >
                      <option value="customer">Customer</option>
                      <option value="internal">Internal</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-green focus:border-transparent"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Tag size={16} className="inline mr-2" />
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => handleInputChange('type', e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-green focus:border-transparent"
                    >
                      <option value="correction">Correction</option>
                      <option value="new_content">New Content</option>
                      <option value="clarification">Clarification</option>
                      <option value="update">Update</option>
                    </select>
                  </div>
                </div>

                {/* Contributor Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User size={16} className="inline mr-2" />
                    Contributor
                  </label>
                  <input
                    type="text"
                    value={formData.contributor}
                    onChange={(e) => handleInputChange('contributor', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-green focus:border-transparent"
                    placeholder="Your name"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {authenticatedUser.email} • {authenticatedUser.role}
                  </p>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Tag size={16} className="inline mr-2" />
                    Tags (Optional)
                  </label>
                  <TagInput
                    tags={formData.tags}
                    onTagsChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
                    availableTags={availableTags}
                    placeholder="Add tags to organize this teaching"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Organize this teaching with tags for easier discovery.
                  </p>
                </div>
              </div>

              {/* Right Column - Pre-load Context */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <AlertCircle size={16} className="inline mr-2" />
                    Pre-load Context (Read-only)
                  </label>
                  <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 max-h-96 overflow-y-auto">
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">
                      {preloadContext}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    This context from your conversation will be included with your teaching to provide background information.
                  </p>
                </div>

                {/* Preview Toggle */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <Book size={16} />
                    {showPreview ? 'Hide' : 'Show'} Content Preview
                  </button>
                </div>

                {/* Content Preview */}
                {showPreview && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Content Preview
                    </label>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">
                        {formData.content || 'Enter teaching content to see preview...'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-eddy-green text-white rounded-lg hover:bg-eddy-green/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating Preview...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Generate Preview
                  </>
                )}
              </button>
            </div>
          </form>
          )}
          
          {previewStep === 'preview' && teachingPreview && (
            <div className="p-6 space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-800 mb-2">Generated Teaching Preview</h4>
                <p className="text-sm text-amber-700 mb-4">
                  Eddy has processed your teaching content and generated the following enhanced version. 
                  Please review and approve, request changes, or cancel.
                </p>
              </div>

              {/* Original vs Generated Comparison */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Original Teaching Content
                  </label>
                  <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">
                      {teachingPreview.userTeachingInput}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Eddy's Enhanced Version
                  </label>
                  <div className="bg-eddy-green/5 border border-eddy-green/20 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">
                      {teachingPreview.proposedContent}
                    </div>
                  </div>
                </div>
              </div>

              {/* Metadata Preview */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-800 mb-2">Teaching Metadata</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-blue-600 font-medium">Question:</span>
                    <p className="text-blue-800">{teachingPreview.originalQuestion}</p>
                  </div>
                  <div>
                    <span className="text-blue-600 font-medium">Audience:</span>
                    <p className="text-blue-800">{teachingPreview.audience}</p>
                  </div>
                  <div>
                    <span className="text-blue-600 font-medium">Priority:</span>
                    <p className="text-blue-800 capitalize">{teachingPreview.priority}</p>
                  </div>
                  <div>
                    <span className="text-blue-600 font-medium">Type:</span>
                    <p className="text-blue-800 capitalize">{teachingPreview.type}</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center gap-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setPreviewStep('clarification')}
                  className="px-6 py-2 border border-amber-500 rounded-lg text-amber-700 hover:bg-amber-50 transition-colors flex items-center gap-2"
                  disabled={isSubmitting}
                >
                  <AlertCircle size={16} />
                  Request Changes
                </button>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-eddy-green text-white rounded-lg hover:bg-eddy-green/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Check size={16} />
                        Approve & Create
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {previewStep === 'clarification' && teachingPreview && (
            <div className="p-6 space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Request Changes to Teaching Content</h4>
                <p className="text-sm text-blue-700">
                  Please describe what changes you'd like Eddy to make to the generated teaching content.
                </p>
              </div>

              {/* Current Generated Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Generated Content
                </label>
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 max-h-32 overflow-y-auto">
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {teachingPreview.proposedContent}
                  </div>
                </div>
              </div>

              {/* Clarification Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What changes would you like?
                </label>
                <textarea
                  value={clarificationText}
                  onChange={(e) => setClarificationText(e.target.value)}
                  placeholder="Please be more specific about..., Add information about..., Change the tone to..., etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-green focus:border-transparent"
                  rows={4}
                  required
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Back to Preview
                </button>
                <button
                  type="button"
                  onClick={handleClarify}
                  disabled={isSubmitting || !clarificationText.trim()}
                  className="px-6 py-2 bg-eddy-blue text-white rounded-lg hover:bg-eddy-blue/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Regenerate with Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeachingForm;